import { useState, useMemo } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Table from '@cloudscape-design/components/table';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Icon from '@cloudscape-design/components/icon';
import Popover from '@cloudscape-design/components/popover';
import {
  parseEmailHeaders,
  getExampleEmailHeader,
  type ParsedEmailHeaders,
  type AuthResult,
  type RelayHop,
} from '@utils/emailHeaderParser';
import { useClipboard } from '@hooks/useClipboard';
import { logger } from '@utils/logger';

export const MailHeaderTab = () => {
  const [inputText, setInputText] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  
  // Use clipboard hook for cross-browser support
  const { pasteFromClipboard, copyToClipboard, error: clipboardError, clearError } = useClipboard();

  const parsedHeaders = useMemo<ParsedEmailHeaders | null>(() => {
    if (!inputText.trim()) {
      return null;
    }

    try {
      return parseEmailHeaders(inputText);
    } catch (error) {
      logger.error('Failed to parse email headers', error);
      return null;
    }
  }, [inputText]);

  const handlePasteFromClipboard = async () => {
    const text = await pasteFromClipboard();
    if (text) {
      setInputText(text);
    }
  };

  const handleTryExample = () => {
    setInputText(getExampleEmailHeader());
  };

  const handleClear = () => {
    setInputText('');
  };

  const handleCopyValue = async (value: string, label: string) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopySuccess(`${label} copied to clipboard!`);
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const getAuthStatusBadge = (status: AuthResult['status']) => {
    const colorMap: Record<AuthResult['status'], 'green' | 'red' | 'grey'> = {
      pass: 'green',
      fail: 'red',
      none: 'grey',
      unknown: 'grey',
    };

    return (
      <Badge color={colorMap[status]}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  const formatDelay = (seconds: number | undefined): string => {
    if (seconds === undefined) return 'N/A';
    if (seconds < 0) return `${Math.round(seconds)}s (negative)`;
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const getTLSIcon = (hop: RelayHop) => {
    if (!hop.tls) {
      return hop.protocol?.includes('HTTPS') ? (
        <Icon name="lock-private" variant="success" />
      ) : (
        <Popover
          header="No encryption"
          content="This hop did not use TLS encryption"
          triggerType="custom"
        >
          <Icon name="unlocked" variant="error" />
        </Popover>
      );
    }

    const version = hop.tls.version.toLowerCase();
    const isStrongTLS = version.includes('1.3') || version.includes('1_3');
    const isWeakTLS = version.includes('1.0') || version.includes('1_0') || version.includes('1.1') || version.includes('1_1');

    return (
      <Popover
        header="TLS Details"
        content={
          <SpaceBetween size="xs">
            <div><strong>Version:</strong> {hop.tls.version}</div>
            <div><strong>Cipher:</strong> {hop.tls.cipher}</div>
            {hop.tls.keySize && <div><strong>Key Size:</strong> {hop.tls.keySize}</div>}
          </SpaceBetween>
        }
        triggerType="custom"
      >
        <Icon 
          name="lock-private" 
          variant={isStrongTLS ? 'success' : isWeakTLS ? 'error' : 'warning'} 
        />
      </Popover>
    );
  };

  const getWarningBadge = (hop: RelayHop) => {
    if (!hop.warnings || hop.warnings.length === 0) {
      return null;
    }

    const colorMap: Record<NonNullable<RelayHop['suspiciousLevel']>, 'red' | 'grey'> = {
      high: 'red',
      medium: 'red',
      low: 'grey',
      none: 'grey',
    };

    return (
      <Popover
        header={`Security Warnings (${hop.suspiciousLevel?.toUpperCase()})`}
        content={
          <SpaceBetween size="xs">
            {hop.warnings.map((warning, idx) => (
              <div key={idx}>• {warning}</div>
            ))}
          </SpaceBetween>
        }
        triggerType="custom"
      >
        <Badge color={colorMap[hop.suspiciousLevel || 'none']}>
          {hop.warnings.length} warning{hop.warnings.length > 1 ? 's' : ''}
        </Badge>
      </Popover>
    );
  };

  return (
    <SpaceBetween size="l">
      {copySuccess && (
        <Alert key="success-alert" type="success" dismissible onDismiss={() => setCopySuccess('')}>
          {copySuccess}
        </Alert>
      )}

      {clipboardError && (
        <Alert key="clipboard-error-alert" type="warning" dismissible onDismiss={clearError}>
          {clipboardError}
        </Alert>
      )}

      <ColumnLayout key="main-layout" columns={inputText && parsedHeaders ? 2 : 1} variant="text-grid" minColumnWidth={300}>
        <div>
          <SpaceBetween size="l">
            <Container
              header={
                <Header
                  variant="h2"
                  description="Paste the raw email headers to analyze delivery path and authentication"
                >
                  Email Headers Input
                </Header>
              }
            >
              <SpaceBetween size="m">
                <Textarea
                  value={inputText}
                  onChange={({ detail }) => setInputText(detail.value)}
                  placeholder="Paste raw email headers here..."
                  rows={12}
                />
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={handlePasteFromClipboard}>Paste from Clipboard</Button>
                  <Button onClick={handleTryExample}>Try Example</Button>
                  <Button onClick={handleClear}>Clear</Button>
                </SpaceBetween>
              </SpaceBetween>
            </Container>

            {parsedHeaders && (
              <Container
                header={
                  <Header variant="h2" description="Key information extracted from headers">
                    Basic Information
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Subject</Box>
                      <Box variant="p">{parsedHeaders.basicInfo.subject || 'N/A'}</Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Date</Box>
                      <Box variant="p">{parsedHeaders.basicInfo.date || 'N/A'}</Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">From</Box>
                      <Box variant="p">
                        <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                          {parsedHeaders.basicInfo.from || 'N/A'}
                        </span>
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">To</Box>
                      <Box variant="p">
                        <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                          {parsedHeaders.basicInfo.to || 'N/A'}
                        </span>
                      </Box>
                    </div>
                  </ColumnLayout>

                  <ExpandableSection headerText="Additional Details" variant="container">
                    <SpaceBetween size="s">
                      <div>
                        <Box variant="awsui-key-label">Message-ID</Box>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            wordBreak: 'break-all',
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            marginTop: '4px',
                          }}
                        >
                          {parsedHeaders.basicInfo.messageId || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Return-Path</Box>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            wordBreak: 'break-all',
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            marginTop: '4px',
                          }}
                        >
                          {parsedHeaders.basicInfo.returnPath || 'N/A'}
                        </div>
                      </div>
                    </SpaceBetween>
                  </ExpandableSection>
                </SpaceBetween>
              </Container>
            )}
          </SpaceBetween>
        </div>

        {parsedHeaders && (
          <div>
            <SpaceBetween size="l">
              {/* Security Issues */}
              {parsedHeaders.securityIssues.length > 0 && (
                <Alert type="warning" header="Security Concerns Detected">
                  <SpaceBetween size="xs">
                    {parsedHeaders.securityIssues.map((issue, index) => (
                      <Box key={index} variant="p">
                        • {issue}
                      </Box>
                    ))}
                  </SpaceBetween>
                </Alert>
              )}

              {/* Authentication Results */}
              <Container
                header={
                  <Header variant="h2" description="Email authentication verification results">
                    Authentication Results
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <ColumnLayout columns={3} variant="text-grid">
                    <div>
                      <SpaceBetween size="xs">
                        <Box variant="awsui-key-label">SPF</Box>
                        {getAuthStatusBadge(parsedHeaders.authentication.spf.status)}
                        {parsedHeaders.authentication.spf.domain && (
                          <Box variant="small">Domain: {parsedHeaders.authentication.spf.domain}</Box>
                        )}
                      </SpaceBetween>
                    </div>
                    <div>
                      <SpaceBetween size="xs">
                        <Box variant="awsui-key-label">DKIM</Box>
                        {getAuthStatusBadge(parsedHeaders.authentication.dkim.status)}
                        {parsedHeaders.authentication.dkim.domain && (
                          <Box variant="small">Domain: {parsedHeaders.authentication.dkim.domain}</Box>
                        )}
                      </SpaceBetween>
                    </div>
                    <div>
                      <SpaceBetween size="xs">
                        <Box variant="awsui-key-label">DMARC</Box>
                        {getAuthStatusBadge(parsedHeaders.authentication.dmarc.status)}
                        {parsedHeaders.authentication.dmarc.policy && (
                          <Box variant="small">Policy: {parsedHeaders.authentication.dmarc.policy}</Box>
                        )}
                      </SpaceBetween>
                    </div>
                  </ColumnLayout>

                  <ExpandableSection headerText="Authentication Details" variant="container">
                    <SpaceBetween size="m">
                      <div>
                        <Box variant="awsui-key-label">SPF Details</Box>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            wordBreak: 'break-all',
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            marginTop: '4px',
                          }}
                        >
                          {parsedHeaders.authentication.spf.details}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">DKIM Details</Box>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            wordBreak: 'break-all',
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            marginTop: '4px',
                          }}
                        >
                          {parsedHeaders.authentication.dkim.details}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">DMARC Details</Box>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            wordBreak: 'break-all',
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            marginTop: '4px',
                          }}
                        >
                          {parsedHeaders.authentication.dmarc.details}
                        </div>
                      </div>
                    </SpaceBetween>
                  </ExpandableSection>
                </SpaceBetween>
              </Container>

              {/* Delivery Statistics */}
              <Container
                header={
                  <Header variant="h2" description="Email delivery timing information">
                    Delivery Statistics
                  </Header>
                }
              >
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <SpaceBetween size="xs">
                      <Box variant="awsui-key-label">Total Delivery Time</Box>
                      <Box variant="h1" fontSize="display-l">
                        {formatDelay(parsedHeaders.timing.totalDelay)}
                      </Box>
                    </SpaceBetween>
                  </div>
                  <div>
                    <SpaceBetween size="xs">
                      <Box variant="awsui-key-label">Number of Hops</Box>
                      <Box variant="h1" fontSize="display-l">
                        {parsedHeaders.relayPath.length}
                      </Box>
                    </SpaceBetween>
                  </div>
                </ColumnLayout>
              </Container>
            </SpaceBetween>
          </div>
        )}
      </ColumnLayout>

      {/* Relay Path Table */}
      {parsedHeaders && parsedHeaders.relayPath.length > 0 && (
        <Container
          header={
            <Header variant="h2" description="Detailed step-by-step path with security analysis">
              Relay Path
            </Header>
          }
        >
          <Table
            columnDefinitions={[
              {
                id: 'hop',
                header: 'Hop',
                cell: (item) => item.hop,
                width: 60,
              },
              {
                id: 'security',
                header: 'Security',
                cell: (item) => (
                  <SpaceBetween direction="horizontal" size="xs">
                    {getTLSIcon(item)}
                    {getWarningBadge(item)}
                  </SpaceBetween>
                ),
                width: 120,
              },
              {
                id: 'from',
                header: 'From Server',
                cell: (item) => (
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 'bold' }}>
                      {item.from}
                    </div>
                    {item.fromIp && (
                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#5f6b7a' }}>
                        {item.fromIp}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                id: 'by',
                header: 'To Server',
                cell: (item) => (
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 'bold' }}>
                      {item.by}
                    </div>
                    {item.byIp && (
                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#5f6b7a' }}>
                        {item.byIp}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                id: 'protocol',
                header: 'Protocol',
                cell: (item) => (
                  <div>
                    <div>{item.protocol || 'N/A'}</div>
                    {item.via && (
                      <div style={{ fontSize: '11px', color: '#5f6b7a' }}>
                        via {item.via}
                      </div>
                    )}
                  </div>
                ),
                width: 140,
              },
              {
                id: 'delay',
                header: 'Delay',
                cell: (item) => formatDelay(item.delay),
                width: 100,
              },
              {
                id: 'details',
                header: 'Details',
                cell: (item) => (
                  (item.tls || item.certificate || item.queueId || item.forAddress) ? (
                    <ExpandableSection headerText="More" variant="footer">
                      <SpaceBetween size="xs">
                        {item.tls && (
                          <div>
                            <Box variant="awsui-key-label">TLS</Box>
                            <div style={{ fontSize: '11px' }}>
                              {item.tls.version} • {item.tls.cipher}
                              {item.tls.keySize && ` • ${item.tls.keySize}`}
                            </div>
                          </div>
                        )}
                        {item.certificate && (
                          <div>
                            <Box variant="awsui-key-label">Certificate</Box>
                            <div style={{ fontSize: '11px' }}>
                              CN: {item.certificate.clientCN}
                              {item.certificate.verificationStatus && ` • ${item.certificate.verificationStatus}`}
                            </div>
                          </div>
                        )}
                        {item.queueId && (
                          <div>
                            <Box variant="awsui-key-label">Queue ID</Box>
                            <div style={{ fontSize: '11px', fontFamily: 'monospace' }}>{item.queueId}</div>
                          </div>
                        )}
                        {item.forAddress && (
                          <div>
                            <Box variant="awsui-key-label">For</Box>
                            <div style={{ fontSize: '11px', fontFamily: 'monospace' }}>{item.forAddress}</div>
                          </div>
                        )}
                      </SpaceBetween>
                    </ExpandableSection>
                  ) : null
                ),
                width: 100,
              },
            ]}
            items={parsedHeaders.relayPath}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No relay information found</b>
              </Box>
            }
            contentDensity="compact"
          />
        </Container>
      )}

      {/* All Headers Table */}
      {parsedHeaders && parsedHeaders.headers.size > 0 && (
        <Container
          header={
            <Header 
              variant="h2" 
              description={`Complete list of all email headers (${parsedHeaders.headers.size} unique headers, ${Array.from(parsedHeaders.headers.values()).reduce((sum, values) => sum + values.length, 0)} total entries)`}
            >
              All Headers
            </Header>
          }
        >
          <Table
            columnDefinitions={[
              {
                id: 'name',
                header: 'Header Name',
                cell: (item) => (
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 'bold' }}>
                    {item.name}
                  </span>
                ),
                width: 250,
              },
              {
                id: 'value',
                header: 'Value',
                cell: (item) => (
                  <div>
                    {item.values.map((value: string, index: number) => (
                      <div
                        key={index}
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          wordBreak: 'break-all',
                          marginBottom: item.values.length > 1 ? '8px' : '0',
                          paddingBottom: item.values.length > 1 && index < item.values.length - 1 ? '8px' : '0',
                          borderBottom: item.values.length > 1 && index < item.values.length - 1 ? '1px solid #e9ebed' : 'none',
                        }}
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                id: 'actions',
                header: 'Actions',
                cell: (item) => (
                  <Button
                    iconName="copy"
                    variant="icon"
                    onClick={() => handleCopyValue(item.values.join('\n'), item.name)}
                  />
                ),
                width: 80,
              },
            ]}
            items={Array.from(parsedHeaders.headers.entries()).map(([name, values]) => ({
              name: name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-'),
              values,
            }))}
            sortingDisabled
            empty={
              <Box textAlign="center" color="inherit">
                <b>No headers found</b>
              </Box>
            }
          />
        </Container>
      )}

    </SpaceBetween>
  );
};

